import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useLayoutEffect,
  useMemo,
} from 'react';
import { Button, Menu } from 'antd';
import { Resizable } from 're-resizable';
import {
  DndContext,
  useDraggable,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import * as THREE from 'three';
import { useSession } from 'next-auth/react';
import { useGetBoughtAssetsByUserQuery } from '@src/components/generated/graphql';
import ImageContent from './components/ImageContent';
import AudioContent from './components/AudioContent';
import VideoContent from './components/VideoContent';
import LiveStreamsContent from './components/LiveStreamsContent';
import BoughtAssetsContent from './components/BoughtAssetsContent';
import MyInventoryContent from './components/MyInventoryContent';
import FreeAssetsGrid from './components/FreeAssetsGrid';
import PaidAssetsContent from './components/PaidAssetsContent';
import WorldsAndPrefabsContent from './components/WorldsAndPrefabsContent';
import ThreeDLibraryContent from './components/ThreeDLibraryContent';
import { UploadAsset } from '../side-bar-menu/my-stuff/upload-asset/UploadAsset';
import { GLTFPackageUpload } from '../side-bar-menu/my-stuff/gltf-package/GLTFPackageUpload';
import MMLObjects from '../side-bar-menu/mml-objects/MMLObjects';
import Portals from '../side-bar-menu/portals/Portals';
import {
  ASSET_TYPE,
  SceneAsset,
  Asset,
} from './react-three-components/sceneAsset';
import { ThemeContext } from '../../../pages/_app';
import { useBranding } from '@src/theme/branding/useBranding';
import styles from './add-to-scene.module.css';
import { makeId } from '../../../util/helpers';
import {
  AddToSceneModalProps,
  TAssetInput,
  MODAL_CONFIG,
} from '../../types/add-to-scene-modal';
import {
  STUDIO_ID_SUFFIX,
  MENU_ITEM_TYPE,
  ASSETS_2022_CDN,
} from '../../../util/constants';
import { SIDE_MENU_EVENTS } from '@src/components/menu/sideMenu';
import ShadersContent from './components/ShadersContent';
import { getCameraRelativePosition } from '../../../util/helpers';

const SCREEN_SHARE_IMAGE = `${ASSETS_2022_CDN}/Global/Studio-ui/studioUis/screenShare.jpg`;

const menuItems = [
  {
    key: MENU_ITEM_TYPE.MY_INVENTORY,
    label: 'My Inventory',
  },
  {
    key: MENU_ITEM_TYPE.BOUGHT_ASSETS,
    label: 'Bought Assets',
  },
  {
    key: MENU_ITEM_TYPE.FREE_ASSETS,
    label: 'Free Assets',
  },
  {
    key: MENU_ITEM_TYPE.PAID_ASSETS,
    label: 'Paid Assets',
  },
  {
    key: MENU_ITEM_TYPE.WORLDS_AND_PREFABS,
    label: 'Worlds & Prefabs',
  },
  {
    key: MENU_ITEM_TYPE.THREE_D_LIBRARY,
    label: '3D Library',
  },
  {
    key: MENU_ITEM_TYPE.IMAGE,
    label: 'Image',
  },
  {
    key: MENU_ITEM_TYPE.AUDIO,
    label: 'Audio',
  },
  {
    key: MENU_ITEM_TYPE.VIDEO,
    label: 'Video',
  },
  {
    key: MENU_ITEM_TYPE.SHADERS,
    label: 'Shaders',
  },
  {
    key: MENU_ITEM_TYPE.UPLOAD_ASSET,
    label: 'Upload asset',
  },
  {
    key: MENU_ITEM_TYPE.GLTF_PACKAGE,
    label: 'GLTF Package',
  },
  {
    key: MENU_ITEM_TYPE.MML_OBJECTS,
    label: 'MML Objects',
  },
  {
    key: MENU_ITEM_TYPE.PORTALS,
    label: 'Add Portal',
  },
  {
    key: MENU_ITEM_TYPE.LIVE_STREAMS,
    label: 'Live Streams',
  },
  {
    key: MENU_ITEM_TYPE.INITIAL_POINT,
    label: 'Add Initial Point',
  },
];

export function DraggableHeader({ children }: { children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'draggable-header',
  });

  return (
    <div ref={setNodeRef} {...listeners} {...attributes}>
      {children}
    </div>
  );
}

export default function AddToSceneModal({
  open,
  onClose,
  setSelectableAssets,
  userEmail,
  worlds,
  selectedWorld,
  buildScene,
  session,
  selectedSceneTemplate,
  loadScene,
  camera,
}: AddToSceneModalProps) {
  const { isDark } = useContext(ThemeContext);
  const { isZepeto, isUnity, isXsolla } = useBranding();
  const { data: userSession } = useSession();
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>(
    MENU_ITEM_TYPE.MY_INVENTORY
  );
  const [isLoading, setIsLoading] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [width, setWidth] = useState(MODAL_CONFIG.DEFAULT_SIZE.width);
  const [height, setHeight] = useState(MODAL_CONFIG.DEFAULT_SIZE.height);
  const [lastWidth, setLastWidth] = useState(MODAL_CONFIG.DEFAULT_SIZE.width);
  const [lastHeight, setLastHeight] = useState(
    MODAL_CONFIG.DEFAULT_SIZE.height
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [shouldRestoreSize, setShouldRestoreSize] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const { data: inventoryData } = useGetBoughtAssetsByUserQuery({
    variables: {
      where: {
        userId: userSession?.user.id ?? '',
        offset: 0,
        limit: 1,
        filter: {
          type: 'all',
          searchInput: undefined,
        },
      },
    },
    fetchPolicy: 'no-cache',
    skip: !userSession?.user.id,
  });

  const hasInventoryItems =
    (inventoryData?.getBoughtAssetsByUser?.assets?.length ?? 0) > 0;

  useLayoutEffect(() => {
    if (shouldRestoreSize) {
      setWidth(lastWidth);
      setHeight(lastHeight);
      setShouldRestoreSize(false);
    }
  }, [shouldRestoreSize, lastWidth, lastHeight]);

  useEffect(() => {
    if (open && !isInitialized) {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      setPosition({
        x: Math.max(0, (windowWidth - MODAL_CONFIG.DEFAULT_SIZE.width) / 2),
        y: Math.max(
          MODAL_CONFIG.NAVBAR_HEIGHT,
          MODAL_CONFIG.NAVBAR_HEIGHT +
            (windowHeight -
              MODAL_CONFIG.NAVBAR_HEIGHT -
              MODAL_CONFIG.DEFAULT_SIZE.height) /
              2
        ),
      });
      setWidth(MODAL_CONFIG.DEFAULT_SIZE.width);
      setHeight(MODAL_CONFIG.DEFAULT_SIZE.height);
      setIsInitialized(true);
    }
  }, [open, isInitialized]);

  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { delta } = event;

    setPosition((prevPosition) => {
      const newX = Math.max(
        0,
        Math.min(window.innerWidth - width, prevPosition.x + delta.x)
      );
      const newY = Math.max(
        MODAL_CONFIG.NAVBAR_HEIGHT,
        Math.min(window.innerHeight - height, prevPosition.y + delta.y)
      );

      return { x: newX, y: newY };
    });
  };

  const baseMenuItems = menuItems;

  const filteredMenuItems = useMemo(
    () =>
      baseMenuItems.filter((item) => {
        return true;
      }),
    [baseMenuItems]
  );

  const finalMenuItems = useMemo(
    () =>
      isXsolla
        ? filteredMenuItems.filter(
            (item) =>
              item.key !== MENU_ITEM_TYPE.MML_OBJECTS &&
              item.key !== MENU_ITEM_TYPE.PAID_ASSETS
          )
        : filteredMenuItems,
    [filteredMenuItems, isXsolla]
  );

  if (!open) return null;

  const handleAddToScene = (assetData: TAssetInput) => {
    const instanceId = makeId(10);
    const position = camera
      ? getCameraRelativePosition(camera, 5)
      : new THREE.Vector3(0, 0, 5);
    let rotation = new THREE.Euler(0, 0, 0);
    let scale = new THREE.Vector3(1, 1, 1);

    const baseAsset: Asset = {
      type: assetData.type || '',
      name: assetData.name || '',
      url: assetData.url || '',
      previewImage: assetData.previewImage || assetData.url || '',
      id: '',
      showOnMinimap: false,
      ref: React.createRef(),
    };
    let assetType: Asset = { ...baseAsset };

    switch (assetData.type) {
      case 'WORLD_SCENE':
        if (loadScene) {
          loadScene(
            assetData.name || '',
            assetData.baseModelURL || assetData.url || '',
            SIDE_MENU_EVENTS.SCENES,
            assetData.sceneThumbnail || assetData.thumbnail || ''
          );
        } else {
          setSelectableAssets({
            type: 'set',
            newState: [],
          });
        }

        if (onClose) {
          onClose();
        }
        return;
      case ASSET_TYPE.PRIMITIVE_TEXTURE.IMAGE_PLANE:
        scale = new THREE.Vector3(5.3, 3, 1);
        assetType = {
          ...baseAsset,
          ...assetData,
          previewImage: assetData.previewImage || assetData.url || '',
          ref: React.createRef(),
        };
        break;
      case ASSET_TYPE.PRIMITIVE_TEXTURE.VIDEO_PLANE:
        scale = new THREE.Vector3(5.3, 3, 1);
        assetType = {
          ...baseAsset,
          ...assetData,
          previewImage:
            assetData.previewImage ||
            assetData.thumbnail ||
            assetData.url ||
            '',
          ref: React.createRef(),
        };
        break;
      case ASSET_TYPE.MISC.POSITIONAL_AUDIO:
        scale = new THREE.Vector3(3, 3, 3);
        assetType = {
          ...baseAsset,
          ...assetData,
          ref: React.createRef(),
        };
        break;
      case ASSET_TYPE.PRIMITIVE_TEXTURE.STREAM:
        scale = new THREE.Vector3(0.01, 0.01, 0.001);
        assetType = {
          ...baseAsset,
          ...assetData,
          url: SCREEN_SHARE_IMAGE,
          ref: React.createRef(),
          metadata: {
            url: assetData.metadata?.url || '',
            type: assetData.metadata?.type || 'stream',
          },
        };
        break;
      case ASSET_TYPE.MML_OBJECT:
        assetType = {
          ...baseAsset,
          ...assetData,
          ref: React.createRef(),
        };
        break;
      case ASSET_TYPE.INITIAL_POINT:
        assetType = {
          ...baseAsset,
          ...assetData,
          ref: React.createRef(),
        };
        break;
      case ASSET_TYPE.MISC.PORTAL:
        assetType = {
          ...baseAsset,
          type: ASSET_TYPE.GLTF.OTHER,
          isPortal: true,
          url: assetData.url || '',
          name: assetData.name || '',
          ref: React.createRef(),
        };
        break;
      case ASSET_TYPE.GLTF.OTHER:
        assetType = {
          ...baseAsset,
          ...assetData,
          ref: React.createRef(),
          name: `${assetData.name}${STUDIO_ID_SUFFIX}${instanceId}`,
        };
        break;
      case ASSET_TYPE.SHADER.WATER:
        scale = new THREE.Vector3(10, 10, 1);
        rotation = new THREE.Euler(-Math.PI / 2, 0, 0); // Rotate to be horizontal
        assetType = {
          ...baseAsset,
          ...assetData,
          ref: React.createRef(),
          name: `${assetData.name}${STUDIO_ID_SUFFIX}${instanceId}`,
        };
        break;
      default:
        assetType = {
          ...baseAsset,
          ...assetData,
          ref: React.createRef(),
        };
    }

    const sceneAsset = React.createElement(SceneAsset, {
      key: `Scene-Asset-${instanceId}`,
      position,
      scale,
      rotation,
      instanceId,
      asset: assetType,
      castShadow: true,
      receiveShadow: true,
    });

    setSelectableAssets({
      type: 'add',
      newElement: sceneAsset as any,
    });

    if (onClose) {
      onClose();
    }
  };

  const renderContent = () => {
    switch (selectedMenuItem) {
      case MENU_ITEM_TYPE.MY_INVENTORY:
        return <MyInventoryContent onAddToScene={handleAddToScene} />;
      case MENU_ITEM_TYPE.BOUGHT_ASSETS:
        return <BoughtAssetsContent onAddToScene={handleAddToScene} />;
      case MENU_ITEM_TYPE.FREE_ASSETS:
        return <FreeAssetsGrid onAddToScene={handleAddToScene} />;
      case MENU_ITEM_TYPE.PAID_ASSETS:
        return (
          <PaidAssetsContent
            onAddToScene={handleAddToScene}
            userEmail={userEmail}
          />
        );
      case MENU_ITEM_TYPE.WORLDS_AND_PREFABS:
        return (
          <WorldsAndPrefabsContent
            onAddToScene={handleAddToScene}
            worlds={worlds}
            selectedWorld={selectedWorld}
          />
        );
      case MENU_ITEM_TYPE.THREE_D_LIBRARY:
        return (
          <ThreeDLibraryContent
            onAddToScene={handleAddToScene}
            buildScene={buildScene}
            session={session}
            selectedSceneTemplate={selectedSceneTemplate}
          />
        );
      case MENU_ITEM_TYPE.IMAGE:
        return <ImageContent onAddToScene={handleAddToScene} />;
      case MENU_ITEM_TYPE.AUDIO:
        return <AudioContent onAddToScene={handleAddToScene} />;
      case MENU_ITEM_TYPE.VIDEO:
        return <VideoContent onAddToScene={handleAddToScene} />;
      case MENU_ITEM_TYPE.SHADERS:
        return <ShadersContent onAddToScene={handleAddToScene} />;
      case MENU_ITEM_TYPE.LIVE_STREAMS:
        return (
          <div className={styles.contentWrapper}>
            <div className={styles.infoSection}>
              <h3>Live Streams</h3>
              <p>
                Embed live streams directly into your virtual world. This allows
                you to display real-time content from platforms like YouTube,
                Twitch, or custom iframe sources within your 3D environment.
              </p>
            </div>
            <LiveStreamsContent onAddToScene={handleAddToScene} />
          </div>
        );
      case MENU_ITEM_TYPE.UPLOAD_ASSET:
        return (
          <div className={styles.uploadWrapper}>
            <UploadAsset
              userEmail={userEmail}
              onCloseModal={() => {}}
              callback={handleAddToScene}
              loading={isLoading}
              setLoading={setIsLoading}
            />
          </div>
        );
      case MENU_ITEM_TYPE.GLTF_PACKAGE:
        return (
          <div className={styles.uploadWrapper}>
            <GLTFPackageUpload
              userEmail={userEmail}
              onCloseModal={() => {}}
              callback={handleAddToScene}
              loading={isLoading}
              setLoading={setIsLoading}
            />
          </div>
        );
      case MENU_ITEM_TYPE.MML_OBJECTS:
        return (
          <div className={styles.contentWrapper}>
            <MMLObjects callback={handleAddToScene} />
          </div>
        );
      case MENU_ITEM_TYPE.PORTALS:
        return (
          <div className={styles.contentWrapper}>
            <div className={styles.infoSection}>
              <h3>Portals</h3>
              <p>
                Portals are how we connect multiple scenes together. Users can
                navigate from one scene to another seamlessly. Simply add a
                portal to your canvas, click on it, and select a target scene.
                <strong> Important:</strong> Portals work in one direction only.
              </p>
            </div>
            <Portals
              loadAsset={(name, url) => {
                handleAddToScene({
                  name,
                  url,
                  type: 'PORTAL',
                  ref: React.createRef(),
                });
              }}
            />
          </div>
        );
      case MENU_ITEM_TYPE.INITIAL_POINT:
        return (
          <div className={styles.contentWrapper}>
            <div className={styles.infoSection}>
              <h3>Initial Point</h3>
              <p>
                Select the starting point for user's avatar to appear when the
                scene is loaded. This determines where visitors will spawn when
                they enter your virtual world.
              </p>
            </div>
            <div className={styles.initialPointContent}>
              <Button
                type='primary'
                onClick={() => {
                  handleAddToScene({
                    type: 'INITIAL_POINT',
                    name: 'Initial Point',
                    ref: React.createRef(),
                  });
                }}
              >
                Add Initial Point
              </Button>
            </div>
          </div>
        );
      default:
        return (
          <div className={styles.emptyContent}>
            Select an item from the menu
          </div>
        );
    }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div
        ref={modalRef}
        className={`${styles.modalRoot} ${isDark ? styles.darkTheme : ''}`}
        data-cy='add-to-scene-modal-root'
        style={{
          left: position.x,
          top: position.y,
        }}
      >
        <Resizable
          size={{ width, height }}
          minWidth={MODAL_CONFIG.MIN_SIZE.width}
          minHeight={MODAL_CONFIG.MIN_SIZE.height}
          maxWidth={Math.min(MODAL_CONFIG.MAX_SIZE.width, window.innerWidth)}
          maxHeight={Math.min(
            MODAL_CONFIG.MAX_SIZE.height,
            window.innerHeight - MODAL_CONFIG.NAVBAR_HEIGHT
          )}
          onResize={(e, direction, ref, d) => {
            setWidth((prev) => prev + d.width);
            setHeight((prev) => prev + d.height);
          }}
          onResizeStop={(e, direction, ref, d) => {
            setWidth((prev) => prev + d.width);
            setHeight((prev) => prev + d.height);
          }}
          enable={{
            top: false,
            right: true,
            bottom: true,
            left: false,
            topRight: false,
            bottomRight: true,
            bottomLeft: false,
            topLeft: false,
          }}
          handleStyles={{
            right: { cursor: 'e-resize' },
            bottom: { cursor: 's-resize' },
            bottomRight: { cursor: 'se-resize' },
          }}
        >
          <div className={styles.modalContainer}>
            <DraggableHeader>
              <div className={styles.modalHeader}>
                <div className={styles.modalTitleContainer}>
                  <span className={styles.modalTitle}>Add to Scene</span>
                </div>
                <div className={styles.modalHeaderBtns}>
                  <button
                    onClick={onClose}
                    className={styles.modalHeaderBtn}
                    title='Close'
                  >
                    ✕
                  </button>
                </div>
              </div>
            </DraggableHeader>
            <div className={styles.modalContent}>
              <div className={styles.sidebar}>
                <Menu
                  mode='vertical'
                  selectedKeys={[selectedMenuItem]}
                  items={finalMenuItems}
                  onClick={({ key }) =>
                    setSelectedMenuItem(key as MENU_ITEM_TYPE)
                  }
                  className={styles.sidebarMenu}
                  theme={isDark ? 'dark' : 'light'}
                />
              </div>
              <div className={styles.mainContent}>{renderContent()}</div>
            </div>
          </div>
        </Resizable>
      </div>
    </DndContext>
  );
}
